import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import addYears from 'date-fns/addYears';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import endOfMonth from 'date-fns/endOfMonth';
import endOfWeek from 'date-fns/endOfWeek';
import format from 'date-fns/format';
import it from 'date-fns/locale/it';
import parseISO from 'date-fns/parseISO';
import startOfMonth from 'date-fns/startOfMonth';
import startOfWeek from 'date-fns/startOfWeek';
import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';

export const AI_CREDITS_PAGE_SIZE = 10;
export const AI_CREDITS_PAGE_SIZE_OPTIONS = [10, 25, 50];

export const AI_CREDITS_RANGE_PRESETS = [
  { value: '7d', label: '7 giorni' },
  { value: '30d', label: '30 giorni' },
  { value: '90d', label: '90 giorni' },
  { value: '12m', label: '12 mesi' },
  { value: 'month', label: 'Mese corrente' },
  { value: 'previousMonth', label: 'Mese precedente' },
];

export const AI_CREDITS_USED_BY_OPTIONS = [
  { value: 'ai_edit', label: 'Fotoritocco Intelligente' },
  { value: 'ai_background', label: 'Crea sfondo con IA' },
  { value: 'background_fusion', label: 'Fusione Sfondo' },
  { value: 'remove_bg', label: 'Rimuovi sfondo' },
  { value: 'magic_eraser', label: 'Pennello correttivo' },
  { value: 'blur_bg', label: 'Sfoca sfondo' },
  { value: 'image_enhancement', label: 'Migliora risoluzione' },
  { value: 'expand', label: 'Espandi scena' },
  { value: 'smile', label: 'Sorriso' },
  { value: 'colorize_bw', label: 'Colora Foto Bianco e Nero' },
  {
    value: 'change_clothes',
    label: 'Cambia abito - v1 (dismesso)',
  },
  { value: 'restore_photo', label: 'Restaura foto' },
];

const AI_CREDITS_USED_BY_LABELS = AI_CREDITS_USED_BY_OPTIONS.reduce(
  (labelsMap, option) => {
    labelsMap[option.value] = option.label;
    return labelsMap;
  },
  {}
);

const numberFormatter = new Intl.NumberFormat('it-IT', {
  maximumFractionDigits: 2,
});

const AI_CREDIT_ATTACHMENT_RAW_FIELDS = [
  'aiEditRawImage',
  'removebgRawImage',
  'magicEraserRawImage',
  'changeClothesRawImage',
  'smileRawImage',
  'colorizeBwRawImage',
  'expandRawImage',
  'enhancementRawImage',
  'rawImage',
];

const AI_CREDIT_USED_BY_TIME_ESTIMATES = {
  ai_edit: {
    aiSeconds: 8,
    savedMinutes: 120,
  },
  ai_background: {
    aiSeconds: 180,
    savedMinutes: 60,
  },
  background_fusion: {
    aiSeconds: 230,
    savedMinutes: 120,
  },
  remove_bg: {
    aiSeconds: 2,
    savedMinutes: 20,
  },
  magic_eraser: {
    aiSeconds: 4,
    savedMinutes: 30,
  },
  blur_bg: {
    aiSeconds: 2,
    savedMinutes: 20,
  },
  image_enhancement: {
    aiSeconds: 4,
    savedMinutes: 20,
  },
  expand: {
    aiSeconds: 9,
    savedMinutes: 90,
  },
  smile: {
    aiSeconds: 3,
    savedMinutes: 20,
  },
  colorize_bw: {
    aiSeconds: 10,
    savedMinutes: 180,
  },
  change_clothes: {
    aiSeconds: 10,
    savedMinutes: 360,
  },
  restore_photo: {
    aiSeconds: 10,
    savedMinutes: 240,
  },
  other: {
    aiSeconds: 5,
    savedMinutes: 20,
  },
};

const AI_CREDIT_FEATURE_TIME_ESTIMATES = [
  {
    aiSeconds: 230,
    matches: ['fusione sfondo'],
    savedMinutes: 120,
  },
  {
    aiSeconds: 180,
    matches: ['crea sfondo con ia'],
    savedMinutes: 60,
  },
  {
    aiSeconds: 10,
    matches: ['cambia abito'],
    savedMinutes: 360,
  },
  {
    aiSeconds: 9,
    matches: ['rimuovi persona', 'rimuovi persone'],
    savedMinutes: 240,
  },
  {
    aiSeconds: 7,
    matches: ['rimuovi oggetto', 'rimuovi oggetti'],
    savedMinutes: 120,
  },
  {
    aiSeconds: 9,
    matches: ['espandi scena'],
    savedMinutes: 90,
  },
  {
    aiSeconds: 10,
    matches: ['colora foto bianco e nero'],
    savedMinutes: 180,
  },
  {
    aiSeconds: 10,
    matches: ['restaura foto'],
    savedMinutes: 240,
  },
];

const AI_CREDITS_FORECAST_HISTORY_DAYS = 365;
const AI_CREDITS_FORECAST_USAGE_WINDOWS = [30, 90, 180];
const AI_CREDITS_FORECAST_RECENT_CREDIT_EVENTS = 4;
const AI_CREDITS_FORECAST_MAX_ITERATIONS = 240;

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'object' && typeof value.iso === 'string') {
    return parseDateValue(value.iso);
  }

  if (typeof value === 'number') {
    const parsedDate = new Date(value);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const parsedDate = parseISO(value);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const escapeRegex = (value = '') => {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const normalizeAiCreditFeature = (feature = '') => {
  return feature
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
};

const isAiCreditRenewalOrder = (order) => {
  const normalizedNote = normalizeAiCreditFeature(order?.note || '');

  return normalizedNote.includes('rinnovo crediti');
};

const buildAiCreditEvents = (orders = []) => {
  const eventsMap = new Map();

  orders.forEach((order) => {
    const paymentReference = String(order?.paymentReference || '').trim();
    const eventKey = paymentReference || order?.objectId;
    const currentEvent = eventsMap.get(eventKey) || {
      createdAt: order.parsedCreatedAt,
      isRenewal: false,
      key: eventKey,
      paymentReference,
      totalAdded: 0,
    };

    currentEvent.createdAt =
      !currentEvent.createdAt || order.parsedCreatedAt > currentEvent.createdAt
        ? order.parsedCreatedAt
        : currentEvent.createdAt;
    currentEvent.totalAdded += Math.max(
      0,
      Number(order?.paidCreditsAmount || 0)
    );
    currentEvent.isRenewal =
      currentEvent.isRenewal || isAiCreditRenewalOrder(order);

    eventsMap.set(eventKey, currentEvent);
  });

  return Array.from(eventsMap.values()).sort((firstEvent, secondEvent) => {
    return firstEvent.createdAt - secondEvent.createdAt;
  });
};

const normalizeAiCreditsBillingFrequency = (frequency = '') => {
  const normalizedFrequency = String(frequency || '')
    .trim()
    .toLowerCase();

  if (
    normalizedFrequency === 'month' ||
    normalizedFrequency === 'monthly' ||
    normalizedFrequency === 'mensile'
  ) {
    return 'month';
  }

  if (
    normalizedFrequency === 'year' ||
    normalizedFrequency === 'yearly' ||
    normalizedFrequency === 'annual' ||
    normalizedFrequency === 'annually' ||
    normalizedFrequency === 'annuale'
  ) {
    return 'year';
  }

  return '';
};

const addAiCreditsBillingCycle = (date, frequency) => {
  if (frequency === 'year') {
    return addYears(date, 1);
  }

  if (frequency === 'month') {
    return addMonths(date, 1);
  }

  return null;
};

const getNextAiCreditsDateFromOrg = (org = {}, now = new Date()) => {
  const safeNow = parseDateValue(now) || new Date();
  const nextBillingDate = parseDateValue(org?.zoho_next_billing);

  if (
    nextBillingDate &&
    differenceInCalendarDays(nextBillingDate, safeNow) >= 0
  ) {
    return nextBillingDate;
  }

  const billingFrequency = normalizeAiCreditsBillingFrequency(
    org?.recurring_billing_frequency
  );
  const subscriptionStartedAt = parseDateValue(org?.subscription_started_at);

  if (!billingFrequency || !subscriptionStartedAt) {
    return null;
  }

  let nextBillingCycleDate = subscriptionStartedAt;
  let iteration = 0;

  while (
    differenceInCalendarDays(nextBillingCycleDate, safeNow) < 0 &&
    iteration < AI_CREDITS_FORECAST_MAX_ITERATIONS
  ) {
    const upcomingCycleDate = addAiCreditsBillingCycle(
      nextBillingCycleDate,
      billingFrequency
    );

    if (!upcomingCycleDate) {
      return null;
    }

    nextBillingCycleDate = upcomingCycleDate;
    iteration += 1;
  }

  if (iteration >= AI_CREDITS_FORECAST_MAX_ITERATIONS) {
    return null;
  }

  return nextBillingCycleDate;
};

export const getDefaultAiCreditsRange = (now = new Date()) => ({
  dateFrom: format(subDays(now, 29), 'yyyy-MM-dd'),
  dateTo: format(now, 'yyyy-MM-dd'),
});

export const getAiCreditsForecastRange = (now = new Date()) => ({
  dateFrom: format(
    subDays(now, AI_CREDITS_FORECAST_HISTORY_DAYS - 1),
    'yyyy-MM-dd'
  ),
  dateTo: format(now, 'yyyy-MM-dd'),
});

export const getAiCreditsPresetRange = (preset, now = new Date()) => {
  if (preset === '7d') {
    return {
      dateFrom: format(subDays(now, 6), 'yyyy-MM-dd'),
      dateTo: format(now, 'yyyy-MM-dd'),
    };
  }

  if (preset === '90d') {
    return {
      dateFrom: format(subDays(now, 89), 'yyyy-MM-dd'),
      dateTo: format(now, 'yyyy-MM-dd'),
    };
  }

  if (preset === '12m') {
    return {
      dateFrom: format(subDays(now, 364), 'yyyy-MM-dd'),
      dateTo: format(now, 'yyyy-MM-dd'),
    };
  }

  if (preset === 'month') {
    return {
      dateFrom: format(startOfMonth(now), 'yyyy-MM-dd'),
      dateTo: format(now, 'yyyy-MM-dd'),
    };
  }

  if (preset === 'previousMonth') {
    const previousMonthDate = subMonths(now, 1);

    return {
      dateFrom: format(startOfMonth(previousMonthDate), 'yyyy-MM-dd'),
      dateTo: format(endOfMonth(previousMonthDate), 'yyyy-MM-dd'),
    };
  }

  return getDefaultAiCreditsRange(now);
};

export const normalizeAiCreditsRange = (range = {}, now = new Date()) => {
  const defaultRange = getDefaultAiCreditsRange(now);
  let dateFrom = range.dateFrom || defaultRange.dateFrom;
  let dateTo = range.dateTo || defaultRange.dateTo;

  if (dateFrom > dateTo) {
    [dateFrom, dateTo] = [dateTo, dateFrom];
  }

  return {
    dateFrom,
    dateTo,
  };
};

export const buildAiCreditsOrdersWhere = (filters = {}) => {
  const { dateFrom, dateTo } = normalizeAiCreditsRange(filters);
  const clauses = [
    {
      createdAt: {
        greaterThanOrEqualTo: `${dateFrom}T00:00:00.000Z`,
        lessThanOrEqualTo: `${dateTo}T23:59:59.999Z`,
      },
    },
    {
      paidCreditsAmount: {
        notEqualTo: 0,
      },
    },
  ];

  if (filters.movementType === 'usage') {
    clauses.push({
      paidCreditsAmount: { lessThan: 0 },
    });
  }

  if (filters.movementType === 'credit') {
    clauses.push({
      paidCreditsAmount: { greaterThan: 0 },
    });
  }

  if (filters.usedBy && filters.usedBy !== 'all') {
    clauses.push({
      usedBy: { equalTo: filters.usedBy },
    });
  }

  if (filters.funeralServiceId) {
    clauses.push({
      note: {
        matchesRegex: `"servizioId":"${escapeRegex(filters.funeralServiceId)}"`,
      },
    });
  }

  if (clauses.length === 1) {
    return clauses[0];
  }

  return {
    AND: clauses,
  };
};

export const buildAiCreditsGiottoProjectsWhere = (filters = {}) => {
  const { dateFrom, dateTo } = normalizeAiCreditsRange(filters);
  const clauses = [
    {
      createdAt: {
        greaterThanOrEqualTo: `${dateFrom}T00:00:00.000Z`,
        lessThanOrEqualTo: `${dateTo}T23:59:59.999Z`,
      },
    },
    {
      status: {
        equalTo: 'active',
      },
    },
  ];

  if (filters.funeralServiceId) {
    clauses.push({
      defunto_pointer: {
        have: {
          objectId: {
            equalTo: filters.funeralServiceId,
          },
        },
      },
    });
  }

  if (clauses.length === 1) {
    return clauses[0];
  }

  return {
    AND: clauses,
  };
};

export const parseAiCreditOrderNote = (note) => {
  if (!note) {
    return null;
  }

  try {
    return JSON.parse(note);
  } catch (err) {
    return null;
  }
};

const getAiCreditOrderTimeEstimate = (order, metadata = null) => {
  const parsedMetadata = metadata || parseAiCreditOrderNote(order?.note);
  const normalizedFeature = normalizeAiCreditFeature(parsedMetadata?.feature);
  const featureEstimate = AI_CREDIT_FEATURE_TIME_ESTIMATES.find((estimate) => {
    return estimate.matches.some((match) => normalizedFeature.includes(match));
  });

  if (featureEstimate) {
    return featureEstimate;
  }

  return (
    AI_CREDIT_USED_BY_TIME_ESTIMATES[order?.usedBy || 'other'] ||
    AI_CREDIT_USED_BY_TIME_ESTIMATES.other
  );
};

export const getAiCreditOrderAttachmentId = (order) => {
  const metadata = parseAiCreditOrderNote(order?.note);

  return metadata?.allegatoId || '';
};

export const getAiCreditOrderSavedMinutesEstimate = (order) => {
  return getAiCreditOrderTimeEstimate(order).savedMinutes;
};

export const getAiCreditOrderAiSecondsEstimate = (order) => {
  return getAiCreditOrderTimeEstimate(order).aiSeconds;
};

export const getAiCreditOrderUsedAmount = (order) => {
  const paidCreditsAmount = Number(order?.paidCreditsAmount || 0);

  return Math.abs(Math.min(0, paidCreditsAmount));
};

export const getAiCreditOrderAddedAmount = (order) => {
  const paidCreditsAmount = Number(order?.paidCreditsAmount || 0);

  return Math.max(0, paidCreditsAmount);
};

export const getAiCreditOrderMovementType = (order) => {
  const usedAmount = getAiCreditOrderUsedAmount(order);
  const addedAmount = getAiCreditOrderAddedAmount(order);

  if (usedAmount > 0 && addedAmount === 0) {
    return 'usage';
  }

  if (addedAmount > 0 && usedAmount === 0) {
    return 'credit';
  }

  if (usedAmount === 0 && addedAmount === 0) {
    return 'neutral';
  }

  return 'adjustment';
};

export const getAiCreditOrderMovementLabel = (order) => {
  const movementType = getAiCreditOrderMovementType(order);

  if (movementType === 'credit') {
    return 'Accredito';
  }

  if (movementType === 'adjustment') {
    return 'Rettifica';
  }

  return 'Utilizzo';
};

export const getAiCreditOrderToolLabel = (orderOrKey) => {
  const usedBy =
    typeof orderOrKey === 'string' ? orderOrKey : orderOrKey?.usedBy || '';
  const order = typeof orderOrKey === 'string' ? null : orderOrKey;
  const metadata = order ? parseAiCreditOrderNote(order.note) : null;

  if (AI_CREDITS_USED_BY_LABELS[usedBy]) {
    return AI_CREDITS_USED_BY_LABELS[usedBy];
  }

  if (metadata?.feature) {
    return metadata.feature;
  }

  if (order && getAiCreditOrderMovementType(order) === 'credit') {
    return 'Ricarica / rinnovo';
  }

  return 'Altro';
};

const getAiCreditOrderFeatureLabel = (order, metadata) => {
  if (!metadata?.feature) {
    return '';
  }

  if (order?.usedBy === 'ai_edit' && metadata.feature === 'Cambia abito') {
    return 'Cambia abito';
  }

  if (order?.usedBy === 'change_clothes') {
    return 'Cambia abito - v1 (dismesso)';
  }

  return metadata.feature;
};

const isRedundantAiCreditDetail = (label, order, featureLabel = '') => {
  const normalizedLabel = normalizeAiCreditFeature(label);

  if (!normalizedLabel) {
    return false;
  }

  const normalizedToolLabel = normalizeAiCreditFeature(
    getAiCreditOrderToolLabel(order)
  );
  const normalizedFeature = normalizeAiCreditFeature(featureLabel);

  return (
    normalizedLabel === normalizedToolLabel ||
    normalizedLabel === normalizedFeature
  );
};

export const getAiCreditOrderFuneralService = (order) => {
  const metadata = parseAiCreditOrderNote(order?.note);

  if (!metadata?.servizioId) {
    return null;
  }

  return {
    id: metadata.servizioId,
    label: metadata.servizioName || metadata.servizioId,
  };
};

export const getAiCreditOrderDetail = (order) => {
  const metadata = parseAiCreditOrderNote(order?.note);
  const featureLabel = getAiCreditOrderFeatureLabel(order, metadata);
  const attachmentId = metadata?.allegatoId || '';
  const attachmentData = attachmentId ? { attachmentId } : {};

  if (metadata?.canvasId) {
    return {
      ...attachmentData,
      href: `/canvas/${metadata.canvasId}`,
      label: metadata.projectTypeName || 'Apri progetto',
    };
  }

  const segments = [];

  if (featureLabel && !isRedundantAiCreditDetail(featureLabel, order)) {
    segments.push(featureLabel);
  }

  if (segments.length > 0) {
    return {
      ...attachmentData,
      label: segments.join(' • '),
    };
  }

  if (attachmentId) {
    return {
      ...attachmentData,
      label: '',
    };
  }

  if (order?.transactionStatus) {
    return {
      ...attachmentData,
      label: order.transactionStatus,
    };
  }

  if (order?.paymentReference) {
    return {
      ...attachmentData,
      label: order.paymentReference,
    };
  }

  if (typeof order?.note === 'string' && !metadata) {
    if (isRedundantAiCreditDetail(order.note, order, featureLabel)) {
      return {
        ...attachmentData,
        label: '—',
      };
    }

    return {
      ...attachmentData,
      label: order.note,
    };
  }

  return {
    ...attachmentData,
    label: '—',
  };
};

const getEditorProjectMetadata = (project) => {
  if (!project?.metadata) {
    return null;
  }

  if (typeof project.metadata === 'string') {
    try {
      return JSON.parse(project.metadata);
    } catch (err) {
      return null;
    }
  }

  return project.metadata;
};

export const getAiCreditsGiottoProjectFuneralService = (project) => {
  const directFuneralService = project?.defunto_pointer;

  if (directFuneralService?.objectId) {
    const label = [directFuneralService.cognome, directFuneralService.nome]
      .filter(Boolean)
      .join(' ');

    return {
      id: directFuneralService.objectId,
      label: label || directFuneralService.objectId,
    };
  }

  const metadata = getEditorProjectMetadata(project);

  if (!metadata?.servizioId) {
    return null;
  }

  return {
    id: metadata.servizioId,
    label: metadata.servizioName || metadata.servizioId,
  };
};

const isAiCreditAttachmentPreviewable = (attachment = {}) => {
  const outputUrl = attachment?.allegato?.url || '';

  if (!outputUrl) {
    return false;
  }

  if (String(attachment.mimeType || '').startsWith('image/')) {
    return true;
  }

  return /\.(avif|gif|jpe?g|png|svg|webp)(\?|$)/i.test(outputUrl);
};

export const getAiCreditAttachmentPreview = (attachment = {}) => {
  if (!isAiCreditAttachmentPreviewable(attachment)) {
    return null;
  }

  const beforeField = AI_CREDIT_ATTACHMENT_RAW_FIELDS.find(
    (field) => attachment?.[field]
  );
  const beforeUrl = beforeField ? attachment[beforeField] : '';

  return {
    afterUrl: attachment.allegato.url,
    beforeUrl: beforeUrl || '',
    hasBefore: Boolean(beforeUrl),
    title: attachment.nome || attachment.allegato.name || 'Allegato AI',
  };
};

export const formatAiCreditsAmount = (value, { signed = false } = {}) => {
  const absoluteValue = numberFormatter.format(Math.abs(value));

  if (!signed) {
    return absoluteValue;
  }

  if (value > 0) {
    return `+${absoluteValue}`;
  }

  if (value < 0) {
    return `-${absoluteValue}`;
  }

  return absoluteValue;
};

const roundUpAiCreditsSuggestion = (credits) => {
  if (credits <= 0) {
    return 0;
  }

  const step = credits <= 20 ? 5 : 10;

  return Math.ceil(credits / step) * step;
};

export const getSuggestedAiCreditsModule = (
  modules = [],
  suggestedCredits = 0
) => {
  const normalizedModules = modules
    .map((module) => ({
      ...module,
      included_quantity: Number(module?.included_quantity || 0),
    }))
    .filter((module) => module.objectId && module.included_quantity > 0)
    .sort((firstModule, secondModule) => {
      return firstModule.included_quantity - secondModule.included_quantity;
    });

  if (normalizedModules.length === 0) {
    return null;
  }

  const safeSuggestedCredits = Math.max(0, Number(suggestedCredits || 0));

  if (safeSuggestedCredits <= 0) {
    return null;
  }

  return (
    normalizedModules.find((module) => {
      return module.included_quantity >= safeSuggestedCredits;
    }) || normalizedModules[normalizedModules.length - 1]
  );
};

export const buildAiCreditsBalanceForecast = ({
  availableCredits = 0,
  now = new Date(),
  orders = [],
  org = {},
} = {}) => {
  const safeNow = parseDateValue(now) || new Date();
  const normalizedAvailableCredits = Math.max(0, Number(availableCredits || 0));
  const enrichedOrders = orders
    .map((order) => ({
      ...order,
      parsedCreatedAt: parseDateValue(order?.createdAt),
    }))
    .filter((order) => order.parsedCreatedAt instanceof Date)
    .sort((firstOrder, secondOrder) => {
      return firstOrder.parsedCreatedAt - secondOrder.parsedCreatedAt;
    });
  const usageOrders = enrichedOrders.filter(
    (order) => getAiCreditOrderUsedAmount(order) > 0
  );
  const creditOrders = enrichedOrders.filter(
    (order) => getAiCreditOrderAddedAmount(order) > 0
  );
  const fallbackWindowDays =
    AI_CREDITS_FORECAST_USAGE_WINDOWS[
      AI_CREDITS_FORECAST_USAGE_WINDOWS.length - 1
    ];
  const usageSample = AI_CREDITS_FORECAST_USAGE_WINDOWS.map((windowDays) => {
    const windowStart = subDays(safeNow, windowDays - 1);
    const usedCredits = usageOrders.reduce((totalUsed, order) => {
      if (
        order.parsedCreatedAt < windowStart ||
        order.parsedCreatedAt > safeNow
      ) {
        return totalUsed;
      }

      return totalUsed + getAiCreditOrderUsedAmount(order);
    }, 0);

    return {
      dailyUsedCredits: usedCredits / windowDays,
      usedCredits,
      windowDays,
    };
  }).find((sample) => sample.usedCredits > 0) || {
    dailyUsedCredits: 0,
    usedCredits: 0,
    windowDays: fallbackWindowDays,
  };

  const daysOfCoverage =
    usageSample.dailyUsedCredits > 0
      ? normalizedAvailableCredits / usageSample.dailyUsedCredits
      : null;
  const creditEvents = buildAiCreditEvents(creditOrders);
  const renewalCreditEvents = creditEvents.filter((event) => event.isRenewal);
  const cadenceCreditEvents =
    renewalCreditEvents.length >= 2 ? renewalCreditEvents : creditEvents;
  const recentCreditEvents = cadenceCreditEvents.slice(
    -AI_CREDITS_FORECAST_RECENT_CREDIT_EVENTS
  );
  const creditIntervals = [];

  for (let index = 1; index < recentCreditEvents.length; index += 1) {
    creditIntervals.push(
      Math.max(
        1,
        differenceInCalendarDays(
          recentCreditEvents[index].createdAt,
          recentCreditEvents[index - 1].createdAt
        )
      )
    );
  }

  const averageCreditIntervalDays =
    creditIntervals.length > 0
      ? Math.max(
          1,
          Math.round(
            creditIntervals.reduce((total, interval) => total + interval, 0) /
              creditIntervals.length
          )
        )
      : null;
  const historicalEstimatedNextCreditDate =
    averageCreditIntervalDays && recentCreditEvents.length > 0
      ? addDays(
          recentCreditEvents[recentCreditEvents.length - 1].createdAt,
          averageCreditIntervalDays
        )
      : null;
  const rawEstimatedNextCreditDate =
    getNextAiCreditsDateFromOrg(org, safeNow) ||
    historicalEstimatedNextCreditDate;
  const daysUntilNextCredit =
    rawEstimatedNextCreditDate &&
    differenceInCalendarDays(rawEstimatedNextCreditDate, safeNow) >= 0
      ? differenceInCalendarDays(rawEstimatedNextCreditDate, safeNow)
      : null;
  const creditsNeededUntilNextCredit =
    daysUntilNextCredit !== null && usageSample.dailyUsedCredits > 0
      ? Math.ceil(usageSample.dailyUsedCredits * daysUntilNextCredit)
      : 0;
  const creditsShortfall = Math.max(
    0,
    creditsNeededUntilNextCredit - normalizedAvailableCredits
  );

  return {
    averageCreditIntervalDays,
    creditsNeededUntilNextCredit,
    creditsShortfall,
    daysOfCoverage,
    daysUntilNextCredit,
    dailyUsedCredits: usageSample.dailyUsedCredits,
    estimatedNextCreditDate:
      daysUntilNextCredit !== null ? rawEstimatedNextCreditDate : null,
    hasNextCreditEstimate: daysUntilNextCredit !== null,
    hasRecentUsage: usageSample.usedCredits > 0,
    enoughUntilNextCredit:
      daysUntilNextCredit !== null && usageSample.dailyUsedCredits > 0
        ? normalizedAvailableCredits >= creditsNeededUntilNextCredit
        : null,
    recentUsedCredits: usageSample.usedCredits,
    suggestedPurchaseCredits: roundUpAiCreditsSuggestion(creditsShortfall),
    usageWindowDays: usageSample.windowDays,
  };
};

const getTimelineBucketMode = (range) => {
  const { dateFrom, dateTo } = normalizeAiCreditsRange(range);
  const days = differenceInCalendarDays(
    parseDateValue(`${dateTo}T00:00:00`),
    parseDateValue(`${dateFrom}T00:00:00`)
  );

  if (days <= 45) {
    return 'day';
  }

  if (days <= 365) {
    return 'week';
  }

  return 'month';
};

const getTimelineBucketData = (date, mode) => {
  if (mode === 'day') {
    return {
      key: format(date, 'yyyy-MM-dd'),
      label: format(date, 'dd MMM', { locale: it }),
      date,
    };
  }

  if (mode === 'week') {
    const bucketStart = startOfWeek(date, { weekStartsOn: 1 });
    const bucketEnd = endOfWeek(date, { weekStartsOn: 1 });

    return {
      key: format(bucketStart, 'yyyy-MM-dd'),
      label: `${format(bucketStart, 'dd MMM', {
        locale: it,
      })} - ${format(bucketEnd, 'dd MMM', { locale: it })}`,
      date: bucketStart,
    };
  }

  const bucketStart = startOfMonth(date);
  const bucketEnd = endOfMonth(date);

  return {
    key: format(bucketStart, 'yyyy-MM-dd'),
    label: `${format(bucketStart, 'MMM yyyy', {
      locale: it,
    })} - ${format(bucketEnd, 'dd MMM', { locale: it })}`,
    date: bucketStart,
  };
};

export const buildAiCreditsHistoryInsights = (
  orders = [],
  range = {},
  editorProjects = [],
  attachments = []
) => {
  const usageOrders = orders.filter(
    (order) => getAiCreditOrderUsedAmount(order) > 0
  );
  const { dateFrom, dateTo } = normalizeAiCreditsRange(range);
  const bucketMode = getTimelineBucketMode(range);
  const timelineMap = new Map();
  const toolsMap = new Map();
  const funeralServicesMap = new Map();
  const editorProjectsMap = new Map();
  const giottoProjectFuneralServicesMap = new Map();
  const attachmentsMap = new Map();
  const activeUsageDays = new Set();
  const rangeDays =
    differenceInCalendarDays(
      parseDateValue(`${dateTo}T00:00:00`),
      parseDateValue(`${dateFrom}T00:00:00`)
    ) + 1;

  let totalUsed = 0;
  let totalAdded = 0;
  let totalEstimatedSavedMinutes = 0;
  let totalEstimatedAiSeconds = 0;

  orders.forEach((order) => {
    totalAdded += getAiCreditOrderAddedAmount(order);
    totalUsed += getAiCreditOrderUsedAmount(order);
  });

  editorProjects.forEach((project) => {
    if (project?.objectId) {
      editorProjectsMap.set(project.objectId, project);
    }

    const funeralService = getAiCreditsGiottoProjectFuneralService(project);

    if (!funeralService?.id) {
      return;
    }

    giottoProjectFuneralServicesMap.set(funeralService.id, funeralService);
  });

  const aiGiottoProjectFuneralServiceIds = new Set();
  const aiGiottoProjectIds = new Set();

  attachments.forEach((attachment) => {
    if (attachment?.objectId) {
      attachmentsMap.set(attachment.objectId, attachment);
    }
  });

  usageOrders.forEach((order) => {
    const createdAt = parseDateValue(order?.createdAt);
    const usedAmount = getAiCreditOrderUsedAmount(order);
    const orderMetadata = parseAiCreditOrderNote(order?.note);
    const attachmentId = orderMetadata?.allegatoId || '';
    const attachment = attachmentId ? attachmentsMap.get(attachmentId) : null;
    const projectId =
      attachment?.editor_canvas_pointer?.objectId || orderMetadata?.canvasId;
    const timeEstimate = getAiCreditOrderTimeEstimate(order, orderMetadata);
    const timelineBucket = getTimelineBucketData(createdAt, bucketMode);
    const existingBucket = timelineMap.get(timelineBucket.key) || {
      ...timelineBucket,
      ordersCount: 0,
      total: 0,
    };

    existingBucket.ordersCount += 1;
    existingBucket.total += usedAmount;
    timelineMap.set(timelineBucket.key, existingBucket);

    const toolKey = order?.usedBy || 'other';
    const toolLabel = getAiCreditOrderToolLabel(order);
    const existingTool = toolsMap.get(toolKey) || {
      estimatedAiSeconds: 0,
      key: toolKey,
      label: toolLabel,
      total: 0,
      ordersCount: 0,
      estimatedSavedMinutes: 0,
    };

    existingTool.total += usedAmount;
    existingTool.ordersCount += 1;
    existingTool.estimatedSavedMinutes += timeEstimate.savedMinutes;
    existingTool.estimatedAiSeconds += timeEstimate.aiSeconds;
    toolsMap.set(toolKey, existingTool);
    totalEstimatedSavedMinutes += timeEstimate.savedMinutes;
    totalEstimatedAiSeconds += timeEstimate.aiSeconds;

    const funeralService = getAiCreditOrderFuneralService(order);

    if (funeralService?.id) {
      const existingFuneralService = funeralServicesMap.get(
        funeralService.id
      ) || {
        id: funeralService.id,
        label: funeralService.label,
        total: 0,
        ordersCount: 0,
      };

      existingFuneralService.total += usedAmount;
      existingFuneralService.ordersCount += 1;
      funeralServicesMap.set(funeralService.id, existingFuneralService);
    }

    activeUsageDays.add(format(createdAt, 'yyyy-MM-dd'));

    if (
      orderMetadata?.servizioId &&
      giottoProjectFuneralServicesMap.has(orderMetadata.servizioId)
    ) {
      aiGiottoProjectFuneralServiceIds.add(orderMetadata.servizioId);
    }

    if (projectId && editorProjectsMap.has(projectId)) {
      aiGiottoProjectIds.add(projectId);
    }
  });

  const giottoProjectFuneralServiceCount = giottoProjectFuneralServicesMap.size;
  const aiGiottoProjectFuneralServiceCount =
    aiGiottoProjectFuneralServiceIds.size;
  const nonAiGiottoProjectFuneralServiceCount = Math.max(
    0,
    giottoProjectFuneralServiceCount - aiGiottoProjectFuneralServiceCount
  );
  const giottoProjectCount = editorProjectsMap.size;
  const aiGiottoProjectCount = aiGiottoProjectIds.size;
  const nonAiGiottoProjectCount = Math.max(
    0,
    giottoProjectCount - aiGiottoProjectCount
  );

  return {
    totalOrders: orders.length,
    usageOrdersCount: usageOrders.length,
    totalUsed,
    totalAdded,
    totalEstimatedAiSeconds,
    totalEstimatedSavedMinutes,
    rangeDays,
    activeUsageDays: activeUsageDays.size,
    activeUsageRate: rangeDays > 0 ? activeUsageDays.size / rangeDays : 0,
    funeralServiceCount: funeralServicesMap.size,
    averageUsageOrdersPerFuneralService:
      funeralServicesMap.size > 0
        ? usageOrders.length / funeralServicesMap.size
        : 0,
    averageUsedPerActiveDay:
      activeUsageDays.size > 0 ? totalUsed / activeUsageDays.size : 0,
    averageUsedPerFuneralService:
      funeralServicesMap.size > 0 ? totalUsed / funeralServicesMap.size : 0,
    creditsPerUsageOrder:
      usageOrders.length > 0 ? totalUsed / usageOrders.length : 0,
    giottoProjectFuneralServiceCount,
    aiGiottoProjectFuneralServiceCount,
    nonAiGiottoProjectFuneralServiceCount,
    aiGiottoProjectFuneralServiceCoverageRate:
      giottoProjectFuneralServiceCount > 0
        ? aiGiottoProjectFuneralServiceCount / giottoProjectFuneralServiceCount
        : 0,
    giottoProjectCount,
    aiGiottoProjectCount,
    nonAiGiottoProjectCount,
    aiGiottoProjectCoverageRate:
      giottoProjectCount > 0 ? aiGiottoProjectCount / giottoProjectCount : 0,
    bucketMode,
    timeline: Array.from(timelineMap.values()).sort((a, b) => a.date - b.date),
    tools: Array.from(toolsMap.values())
      .sort((a, b) => {
        if (b.ordersCount !== a.ordersCount) {
          return b.ordersCount - a.ordersCount;
        }

        return b.total - a.total;
      })
      .slice(0, 6),
    funeralServices: Array.from(funeralServicesMap.values())
      .sort((a, b) => {
        if (b.ordersCount !== a.ordersCount) {
          return b.ordersCount - a.ordersCount;
        }

        return b.total - a.total;
      })
      .slice(0, 6),
  };
};
